/**
 * Welle B (Punkt 10) — Upload-Mapping: welches Datei-Format wird auf welchen
 * RYFE-`kind` abgebildet. Die eine Quelle der Wahrheit für „wenn Format X, dann
 * Typ Y" (Nutzer-Entscheidung 2026-08-25):
 *
 *   - .md .markdown .txt          → `md`     (Text 1:1, öffnet im MD-Editor)
 *   - Bilder (png/jpg/svg/…)      → `image`  (Blob 1:1)
 *   - .excalidraw                 → `board`  (Excalidraw-Szene 1:1)
 *   - Tabellen (.csv .xlsx .xls)  → `file`   (Original-Blob, NUR Download —
 *                                   bewusst KEINE automatische Liste, da
 *                                   verlustbehaftet; Liste legt man bewusst an
 *                                   und nutzt den CSV-Import)
 *   - alles andere (pdf/docx/zip/…) → `file` (opaque, gespeichert + gelistet,
 *                                   „Öffnen" = Download; CLAUDE.md §5)
 *
 * `kind` bleibt ein offener Diskriminator (§5): unbekannte Typen sind gültig,
 * nur ihr Öffnen richtet sich nach vorhandenen Editoren/Viewern.
 */

export interface UploadMapping {
  /** Ziel-`kind` der angelegten Datei. */
  kind: string;
  /** Content-Type, unter dem der Blob auf S3 abgelegt wird. */
  contentType: string;
}

const IMAGE_EXT = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
  "bmp",
]);

const MD_EXT = new Set(["md", "markdown", "txt"]);

// Best-effort Content-Type je Endung (der Browser-`file.type` ist oft leer,
// z. B. bei .md oder .excalidraw) — sonst Fallback auf den mitgelieferten MIME.
const MIME_BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  avif: "image/avif",
  bmp: "image/bmp",
  md: "text/markdown",
  markdown: "text/markdown",
  txt: "text/markdown",
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  json: "application/json",
  zip: "application/zip",
  excalidraw: "application/json",
};

/** Kleingeschriebene Datei-Endung ohne Punkt, oder "" wenn keine vorhanden. */
export const extensionOf = (filename: string): string => {
  const m = /\.([A-Za-z0-9]+)$/.exec(filename.trim());
  return m ? m[1].toLowerCase() : "";
};

/**
 * AP-7 (P17.2) — plan a recursive folder upload. From a set of files (each with
 * an optional `webkitRelativePath` like "Root/sub/datei.pdf") derive:
 *   - `folders`: the relative directory paths to create, PARENTS BEFORE CHILDREN
 *     (so the id-map is always populated before a child needs it);
 *   - `files`: each file paired with the relative directory path it belongs in
 *     ("" = the chosen destination folder itself).
 * Reine Funktion (kein DOM/Netz), damit die Baum-Ableitung testbar ist. Der
 * Aufrufer legt die Ordner an (Pfad→id-Map) und lädt dann jede Datei hoch.
 */
export interface UploadTreeFile {
  file: File;
  dirPath: string;
}
export interface UploadTree {
  folders: string[];
  files: UploadTreeFile[];
}
export const buildUploadTree = (files: File[]): UploadTree => {
  const folderSet = new Set<string>();
  const out: UploadTreeFile[] = [];
  for (const file of files) {
    const rel = (
      (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
      file.name
    ).replace(/^\/+/, "");
    const parts = rel.split("/").filter(Boolean);
    const dirParts = parts.slice(0, -1);
    const dirPath = dirParts.join("/");
    // Register every ancestor directory path so parents exist before children.
    for (let i = 1; i <= dirParts.length; i++) {
      folderSet.add(dirParts.slice(0, i).join("/"));
    }
    out.push({ file, dirPath });
  }
  const folders = [...folderSet].sort(
    (a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b),
  );
  return { folders, files: out };
};

/**
 * Bildet einen Upload (Dateiname + optionaler Browser-MIME) auf `kind` +
 * Content-Type ab. Reine Funktion, damit sie testbar ist.
 */
export const mapUpload = (filename: string, mime?: string): UploadMapping => {
  const ext = extensionOf(filename);
  const fallback = MIME_BY_EXT[ext] || mime || "application/octet-stream";
  if (MD_EXT.has(ext)) {
    return { kind: "md", contentType: "text/markdown" };
  }
  if (IMAGE_EXT.has(ext) || (mime || "").startsWith("image/")) {
    return { kind: "image", contentType: fallback };
  }
  if (ext === "excalidraw") {
    return { kind: "board", contentType: "application/json" };
  }
  // Entscheidung: Tabellen + alle übrigen Formate bleiben Original-Blob (`file`),
  // gespeichert + gelistet, „Öffnen" = Download.
  return { kind: "file", contentType: fallback };
};
